import { useState } from 'react';
import { CircleCheck, RotateCcw, X, LoaderCircle } from 'lucide-react';
import { usePermissions } from '@/lib/usePermissions';

interface CompletionDialogProps {
  submissionLabel: string;
  sectionsPct: number | null;
  contentDonePct: number | null;
  documentDonePct: number | null;
  consistencyPct: number | null;
  isReopening?: boolean;
  onConfirm: (comment: string) => Promise<void>;
  onClose: () => void;
}

function MetricRow({ label, pct }: { label: string; pct: number | null }) {
  if (pct === null) return (
    <div className="flex justify-between text-sm">
      <span className="text-slate-500">{label}</span>
      <span className="text-slate-400">Not scanned</span>
    </div>
  );
  const passing = pct >= 100;
  return (
    <div className="flex justify-between text-sm">
      <span className="text-slate-600">{label}</span>
      <span className={passing ? 'font-medium text-emerald-600' : 'font-medium text-red-600'}>
        {pct}%{passing ? '' : ' — FAILING'}
      </span>
    </div>
  );
}

export function CompletionDialog({
  submissionLabel,
  sectionsPct,
  contentDonePct,
  documentDonePct,
  consistencyPct,
  isReopening = false,
  onConfirm,
  onClose,
}: CompletionDialogProps) {
  const { canCompleteSubmission } = usePermissions();
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const isOverride =
    !isReopening &&
    [sectionsPct, contentDonePct, documentDonePct, consistencyPct].some(
      (p) => p !== null && p < 100,
    );

  // Comment is mandatory for overrides and reopens
  const commentRequired = isOverride || isReopening;
  const commentValid = !commentRequired || comment.trim().length >= 10;

  const handleConfirm = async () => {
    setSubmitting(true);
    setError('');
    try {
      await onConfirm(comment.trim());
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'An unexpected error occurred');
    } finally {
      setSubmitting(false);
    }
  };

  const Icon = isReopening ? RotateCcw : CircleCheck;
  const title = isReopening ? 'Reopen Submission' : 'Mark Submission Complete';
  const action = isReopening ? 'Reopen' : 'Mark Complete';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <Icon className="w-5 h-5 text-brand-orange" />
            <h2 className="text-lg font-bold text-brand-charcoal">{title}</h2>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-4 space-y-4">
          <p className="text-sm text-slate-600">
            {isReopening
              ? `Reopen ${submissionLabel}. This will clear the completion status and allow further edits.`
              : `Mark ${submissionLabel} as complete. This action will be recorded in the audit trail.`}
          </p>

          {/* Metrics summary — not shown for reopen */}
          {!isReopening && (
            <div className="bg-slate-50 rounded-lg p-4 space-y-2">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Current Metrics</p>
              <MetricRow label="Sections Present" pct={sectionsPct} />
              <MetricRow label="% Content Done" pct={contentDonePct} />
              <MetricRow label="% Document Done" pct={documentDonePct} />
              <MetricRow label="Consistency" pct={consistencyPct} />
            </div>
          )}

          {/* Override warning */}
          {isOverride && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <p className="text-sm text-amber-800">
                One or more readiness metrics are below 100%. A comment explaining the
                override rationale is required for Part 11 compliance.
              </p>
            </div>
          )}

          {/* Reopen warning */}
          {isReopening && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <p className="text-sm text-amber-800">
                Reopening a completed submission is a significant regulatory action.
                A comment documenting the rationale is required.
              </p>
            </div>
          )}

          {/* Comment textarea */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              {commentRequired ? 'Comment (required)' : 'Comment (optional)'}
            </label>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder={
                isReopening
                  ? 'Explain why this submission is being reopened...'
                  : isOverride
                    ? 'Explain why the submission is being completed with failing metrics...'
                    : 'Optional comment for the audit trail...'
              }
              rows={3}
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-brand-orange/30 focus:border-brand-orange"
            />
            {commentRequired && comment.trim().length > 0 && comment.trim().length < 10 && (
              <p className="mt-1 text-xs text-red-500">Minimum 10 characters required</p>
            )}
          </div>

          {error && (
            <div className="px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-slate-200">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={!commentValid || submitting || !canCompleteSubmission}
            title={!canCompleteSubmission ? 'Requires Lead role' : undefined}
            className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-brand-orange rounded-lg hover:bg-orange-600 disabled:opacity-50${!canCompleteSubmission ? ' cursor-not-allowed' : ''}`}
          >
            {submitting && <LoaderCircle className="w-4 h-4 animate-spin" />}
            {action}
          </button>
        </div>
      </div>
    </div>
  );
}
